import { useState } from "react";
import { fehlerText } from "@vimaana/ui";
import { ABO_FARBEN, useAboAendern, useAboAnlegen, useAboEntfernen, useKalenderAbos } from "../../hooks/useKalenderAbos";
import "../../styles/kalenderabo.css";

/**
 * Kalender abonnieren — der eigene Apple-Kalender (oder Google, Feiertage,
 * Schulferien) unter dem gewählten Tag im Planer. Vorbild ist Syncus
 * (`AboKalenderCard.jsx`).
 *
 * Die Abos gehören dem KONTO: Benjamin sieht seine, Volker seine.
 */
export default function KalenderAboSektion() {
  const abos = useKalenderAbos();
  const anlegen = useAboAnlegen();
  const aendern = useAboAendern();
  const entfernen = useAboEntfernen();

  const [formular, setFormular] = useState(false);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [farbe, setFarbe] = useState(ABO_FARBEN[0]);
  const [entfernenId, setEntfernenId] = useState<number | null>(null);
  const [anleitung, setAnleitung] = useState(false);

  const hinzufuegen = () => anlegen.mutate(
    { name, url, farbe },
    { onSuccess: () => { setName(""); setUrl(""); setFarbe(ABO_FARBEN[0]); setFormular(false); } },
  );

  const alle = abos.data ?? [];
  const eigene = alle.filter((a) => a.eigen);
  const fremde = alle.filter((a) => !a.eigen);

  return (
    <section className="ka-card">
      <div className="ka-h2">Kalender abonnieren</div>
      <p className="ka-text">
        Deine eigenen Termine im Planer — unter dem Tag, den du antippst. Nur du siehst sie;
        Kontor liest nur und speichert keine Termine.
      </p>

      {abos.isPending ? (
        <p className="ka-text ka-leise">Wird geladen …</p>
      ) : abos.isError ? (
        <p className="ka-fehler">{fehlerText(abos.error)}</p>
      ) : (
        <ul className="ka-liste">
          {eigene.map((a) => (
            <li key={a.id} className="ka-zeile">
              <label className="ka-schalter">
                <input
                  type="checkbox" checked={a.aktiv}
                  onChange={() => aendern.mutate({ id: a.id, aktiv: !a.aktiv })}
                  aria-label={`${a.name} im Planer zeigen`}
                />
                <span className="ka-punkt" style={{ background: a.farbe }} />
                <span className="ka-name">
                  <strong>{a.name}</strong>
                  <span>{a.aktiv ? "wird angezeigt" : "ausgeblendet"}{a.geteilt ? " · fürs Team freigegeben" : ""}{a.sperrt ? " · macht dich „nicht da“" : ""}</span>
                </span>
              </label>
              {entfernenId === a.id ? (
                <span className="ka-sicher">
                  <button type="button" className="ka-btn ka-btn-rot" disabled={entfernen.isPending}
                    onClick={() => entfernen.mutate(a.id, { onSuccess: () => setEntfernenId(null) })}>
                    {entfernen.isPending ? "…" : "Entfernen"}
                  </button>
                  <button type="button" className="ka-btn" onClick={() => setEntfernenId(null)}>Nein</button>
                </span>
              ) : (
                <button type="button" className="ka-btn" onClick={() => setEntfernenId(a.id)} aria-label={`${a.name} entfernen`}>
                  Entfernen
                </button>
              )}
            </li>
          ))}
          {eigene.length === 0 && <li className="ka-text ka-leise">Noch kein Kalender abonniert.</li>}
        </ul>
      )}

      {eigene.length > 0 && (
        <details className="ka-freigabe">
          <summary>Fürs Team freigeben</summary>
          <p className="ka-text ka-leise">
            Ein freigegebener Kalender erscheint bei allen, die Kontor benutzen — im Planer unter
            dem Tag. Sie sehen die Termine, nicht die Adresse, und können nichts daran ändern.
          </p>
          {eigene.map((a) => (
            <label key={a.id} className="ka-schalter ka-schalter-zeile">
              <input
                type="checkbox" checked={a.geteilt}
                onChange={() => aendern.mutate({ id: a.id, geteilt: !a.geteilt })}
              />
              <span className="ka-punkt" style={{ background: a.farbe }} />
              <span className="ka-name"><strong>{a.name}</strong></span>
            </label>
          ))}
        </details>
      )}

      {eigene.length > 0 && (
        <details className="ka-freigabe">
          <summary>Als „Nicht da“ verwenden</summary>
          <p className="ka-text ka-leise">
            Termine aus diesen Kalendern machen dich im Planer nicht verfügbar — wie ein „Nicht da“,
            das du in Kontor einträgst. Das Team sieht nur „nicht da“, nicht den Titel. Am besten
            ein eigener Kalender dafür, nicht der ganze private.
          </p>
          {eigene.map((a) => (
            <label key={a.id} className="ka-schalter ka-schalter-zeile">
              <input
                type="checkbox" checked={a.sperrt}
                onChange={() => aendern.mutate({ id: a.id, sperrt: !a.sperrt })}
              />
              <span className="ka-punkt" style={{ background: a.farbe }} />
              <span className="ka-name"><strong>{a.name}</strong></span>
            </label>
          ))}
        </details>
      )}

      {fremde.length > 0 && (
        <div className="ka-fremde">
          <div className="ka-h2">Vom Team freigegeben</div>
          <ul className="ka-liste">
            {fremde.map((a) => (
              <li key={a.id} className="ka-zeile">
                <span className="ka-punkt" style={{ background: a.farbe }} />
                <span className="ka-name">
                  <strong>{a.name}</strong>
                  <span>von {a.besitzer || "einer Kollegin oder einem Kollegen"} · wird im Planer angezeigt</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {(aendern.isError || entfernen.isError) && (
        <p className="ka-fehler">{fehlerText(aendern.error || entfernen.error)}</p>
      )}

      {formular ? (
        <div className="ka-formular">
          <input className="ka-feld" value={name} onChange={(e) => setName(e.target.value)} maxLength={60}
            placeholder="Name, z. B. Privat" />
          <input className="ka-feld ka-mono" value={url} onChange={(e) => setUrl(e.target.value)}
            inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck={false}
            placeholder="webcal://… oder https://…" />
          <div className="ka-farben" role="radiogroup" aria-label="Farbe">
            {ABO_FARBEN.map((f) => (
              <button key={f} type="button" role="radio" aria-checked={farbe === f} aria-label={`Farbe ${f}`}
                className={`ka-farbe${farbe === f ? " ka-farbe-an" : ""}`} style={{ background: f }}
                onClick={() => setFarbe(f)} />
            ))}
          </div>
          {anlegen.isError && <p className="ka-fehler">{fehlerText(anlegen.error)}</p>}
          <div className="ka-knoepfe">
            <button type="button" className="vg-btn vg-btn-primary ka-gross" disabled={!url.trim() || anlegen.isPending} onClick={hinzufuegen}>
              {anlegen.isPending ? "Wird geprüft …" : "Abonnieren"}
            </button>
            <button type="button" className="ka-btn ka-gross" onClick={() => { setFormular(false); anlegen.reset(); }}>Abbrechen</button>
          </div>
        </div>
      ) : (
        <button type="button" className="ka-btn ka-gross ka-neu" onClick={() => setFormular(true)}>＋ Kalender hinzufügen</button>
      )}

      <button type="button" className="ka-anleitung-kopf" aria-expanded={anleitung} onClick={() => setAnleitung(!anleitung)}>
        {anleitung ? "▾" : "▸"} Wo finde ich die Adresse meines Apple-Kalenders?
      </button>
      {anleitung && (
        <div className="ka-anleitung">
          <ol>
            <li>Auf dem iPhone die App <strong>Kalender</strong> öffnen, unten auf <strong>Kalender</strong> tippen.</li>
            <li>Neben dem gewünschten iCloud-Kalender auf <strong>ⓘ</strong> tippen.</li>
            <li><strong>Öffentlicher Kalender</strong> einschalten, dann <strong>Link teilen …</strong> → <strong>Kopieren</strong>.</li>
            <li>Hier auf „Kalender hinzufügen" tippen und die Adresse einfügen.</li>
          </ol>
          <p>
            Das geht nur mit iCloud-Kalendern. Wer den Link kennt, kann den Kalender lesen — gib ihn
            nicht weiter. Ausschalten von „Öffentlicher Kalender" macht den Link ungültig.
          </p>
          <p>
            Google-Kalender: in den Einstellungen des Kalenders die <strong>geheime Adresse im
            iCal-Format</strong> kopieren. Neue Termine erscheinen nach wenigen Minuten.
          </p>
        </div>
      )}
    </section>
  );
}
